import { useEffect, useState } from "react";
import { X, Loader2, Mail, Phone, Cake, BookUser } from "lucide-react";
import api, { formatError } from "@/lib/api";
import Avatar from "@/components/Avatar";

/**
 * GroupContactListModal — read-only panel that lists every member of
 * a group and the contact info each member chose to share.
 *
 * Visibility rules (enforced server-side):
 *   • name + avatar: always visible
 *   • email + phone: only if member has `show_contact_publicly`
 *   • birthday    : only if member has `show_birthday_publicly`
 */
export default function GroupContactListModal({ groupId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);
    api.get(`/groups/${groupId}/contact-list`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatError(e)))
      .finally(() => setBusy(false));
  }, [groupId]);

  const formatBirthday = (b) => {
    // "06-15" → "June 15"; "1992-06-15" → "June 15, 1992"
    if (!b) return "";
    const parts = b.split("-").map(Number);
    const MONTHS = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${MONTHS[m - 1]} ${d}, ${y}`;
    }
    const [m, d] = parts;
    return `${MONTHS[m - 1]} ${d}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="contact-list-modal"
    >
      <div
        className="bd-surface p-6 w-full max-w-lg"
        style={{ background: "var(--bg-elev)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BookUser size={18} style={{ color: "var(--primary)" }} />
            <h2 className="text-xl font-extrabold" style={{ letterSpacing: "-0.02em" }}>
              Contact list{data?.group_name ? ` · ${data.group_name}` : ""}
            </h2>
          </div>
          <button onClick={onClose} className="bd-btn bd-btn-ghost"
                  style={{ padding: "0.4rem" }} data-testid="contact-list-close">
            <X size={14} />
          </button>
        </div>

        <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
          Only fields each member chose to share are shown. Update yours under{" "}
          <strong>Profile · Group visibility</strong>.
        </p>

        {busy && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-dim)" }}>
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {err && <div className="text-sm" style={{ color: "#ff3b30" }}>{err}</div>}

        {data && (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="contact-list">
            {data.members.map((m) => (
              <li
                key={m.user_id}
                className="flex items-start gap-3 p-3 rounded"
                style={{ border: "1px solid var(--border)" }}
                data-testid={`contact-row-${m.user_id}`}
              >
                <Avatar name={m.name} picture={m.picture} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.name}</div>
                  <div className="text-xs flex flex-col gap-1 mt-1" style={{ color: "var(--text-dim)" }}>
                    {m.birthday && (
                      <span className="flex items-center gap-1.5">
                        <Cake size={11} /> {formatBirthday(m.birthday)}
                      </span>
                    )}
                    {m.email && (
                      <a href={`mailto:${m.email}`}
                         className="flex items-center gap-1.5"
                         style={{ color: "var(--primary)" }}>
                        <Mail size={11} /> {m.email}
                      </a>
                    )}
                    {m.phone && (
                      <a href={`tel:${m.phone}`}
                         className="flex items-center gap-1.5 bd-mono"
                         style={{ color: "var(--primary)" }}>
                        <Phone size={11} /> {m.phone}
                      </a>
                    )}
                    {!m.email && !m.phone && !m.birthday && (
                      <span style={{ opacity: 0.6 }}>
                        Hasn&apos;t shared any contact info
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {data.members.length === 0 && (
              <li className="text-sm" style={{ color: "var(--text-dim)" }}>
                No members yet.
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
